"use client";

import { useMarketingAnalytics } from "@analytics";
import { MARKETING_HEADER_CHROME_PORTAL_ATTR } from "@marketing/shared/lib/marketing-site-header-outside-interaction";
import { CONTENT_TOOLTIP_CHROME, ContentTooltip } from "@repo/ui";
import { Button } from "@repo/ui/components/button";
import { CircleButton } from "@repo/ui/components/custom/circle-button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuTrigger,
} from "@repo/ui/components/dropdown-menu";
import { runCircularRevealViewTransition } from "@repo/ui/components/shadcn/circle-view-transition";
import { HardDriveIcon, MoonIcon, SunIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useTheme } from "next-themes";
import { useRef, useState } from "react";
import { useIsClient } from "usehooks-ts";

export type ColorModeToggleAppearance = "ghost" | "circle-dark" | "circle-glass";

interface ColorModeToggleProps {
	/**
	 * `ghost` — marketing / default (Button ghost icon).
	 * `circle-glass` — SaaS header glass circle (`CircleButton` glass system sm).
	 * `circle-dark` — legacy navy solid (alias kept for older call sites).
	 */
	appearance?: ColorModeToggleAppearance;
}

export function ColorModeToggle({
	appearance = "ghost",
}: ColorModeToggleProps) {
	const t = useTranslations("common.colorMode");
	const { resolvedTheme, setTheme, theme } = useTheme();
	const { landingColorModeChanged } = useMarketingAnalytics();
	const [value, setValue] = useState<string>(theme ?? "system");
	const [tooltipOpen, setTooltipOpen] = useState(false);
	const pointerInsideRef = useRef(false);
	const triggerRef = useRef<HTMLButtonElement | null>(null);
	const isClient = useIsClient();

	const colorModeOptions = [
		{
			value: "system",
			label: "System",
			icon: HardDriveIcon,
		},
		{
			value: "light",
			label: "Light",
			icon: SunIcon,
		},
		{
			value: "dark",
			label: "Dark",
			icon: MoonIcon,
		},
	];

	if (!isClient) {
		return null;
	}

	const icon =
		resolvedTheme === "light" ? (
			<SunIcon className="size-4" aria-hidden />
		) : (
			<MoonIcon className="size-4" aria-hidden />
		);

	const triggerSharedProps = {
		ref: triggerRef,
		"data-test": "color-mode-toggle" as const,
		"aria-label": t("ariaLabel"),
		onPointerEnter: () => {
			pointerInsideRef.current = true;
			setTooltipOpen(true);
		},
		onPointerLeave: () => {
			pointerInsideRef.current = false;
			setTooltipOpen(false);
		},
	};

	return (
		<DropdownMenu modal={false}>
			<ContentTooltip
				{...CONTENT_TOOLTIP_CHROME}
				side="bottom"
				open={tooltipOpen}
				onOpenChange={(next) => {
					// Dialog autofocus opens tooltips via focus — only allow hover.
					if (next && !pointerInsideRef.current) {
						return;
					}
					setTooltipOpen(next);
				}}
				content={t("tooltip")}
			>
				<DropdownMenuTrigger asChild>
					{appearance === "circle-glass" ||
					appearance === "circle-dark" ? (
						<CircleButton
							{...triggerSharedProps}
							chrome={
								appearance === "circle-glass"
									? "glass"
									: "solid"
							}
							tone={
								appearance === "circle-glass"
									? "system"
									: "dark"
							}
							size="sm"
						>
							{icon}
						</CircleButton>
					) : (
						<Button
							{...triggerSharedProps}
							variant="ghost"
							size="icon"
						>
							{icon}
						</Button>
					)}
				</DropdownMenuTrigger>
			</ContentTooltip>

			<DropdownMenuContent
				className="z-[260]"
				{...{ [MARKETING_HEADER_CHROME_PORTAL_ATTR]: "" }}
			>
				<DropdownMenuRadioGroup
					value={value}
					onValueChange={(next) => {
						setValue(next);
						landingColorModeChanged({ mode: next });
						void runCircularRevealViewTransition(() => {
							setTheme(next);
						}, triggerRef.current);
					}}
				>
					{colorModeOptions.map((option) => (
						<DropdownMenuRadioItem
							key={option.value}
							value={option.value}
							data-test={`color-mode-toggle-item-${option.value}`}
						>
							<option.icon className="mr-2 size-4 opacity-50" />{" "}
							{option.label}
						</DropdownMenuRadioItem>
					))}
				</DropdownMenuRadioGroup>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
